import type { PersonFileListItem } from '@vigeo/shared';
import { fetchFileContent, FileRequestError, type FileProblem } from './api.js';
import { fallbackDownloadName } from './model.js';

/**
 * How long a download's object URL lives after the click. Revoking in the same task cancels
 * the save in some browsers; a second is ample and the bytes are released straight after.
 */
export const DOWNLOAD_URL_LIFETIME_MS = 1000;

export const problemCopy: Record<FileProblem, string> = {
  'not-found': 'This file is not available. It may have been moved, or you may not have access to it.',
  unavailable: 'File storage is unavailable right now. Try again in a few minutes.',
  failed: 'The file could not be opened. Try again, and contact support if it keeps happening.',
};

export function problemOf(error: unknown): FileProblem {
  return error instanceof FileRequestError ? error.problem : 'failed';
}

/**
 * Saves a file through the API with `disposition=attachment`. The bytes come back as a Blob and
 * leave through a short-lived object URL on a detached anchor: the browser never navigates to
 * the API, so no file URL reaches the address bar or history.
 */
export async function downloadFile(personId: string, item: Pick<PersonFileListItem, 'fileId' | 'displayName' | 'contentType'>): Promise<void> {
  const content = await fetchFileContent(personId, item.fileId, 'attachment');
  const url = URL.createObjectURL(new Blob([content.bytes], { type: content.contentType }));
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = content.fileName ?? fallbackDownloadName(item);
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS);
  }
}
